import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { YearbookProvider } from './context/YearbookContext';
import { ArrowLeftIcon, HomeIcon } from '@heroicons/react/24/outline';
import { Logo } from './components/common';
import { initGA, initMixpanel } from './utils/analytics';
import { usePageTracking } from './hooks';

// Pages
import LandingPage from './pages/LandingPage';
import TypeSelectPage from './pages/TypeSelectPage';
import UploadPage from './pages/UploadPage';
import AnalysisPage from './pages/AnalysisPage';
import BookSetupPage from './pages/BookSetupPage';
import PreviewPage from './pages/PreviewPage';
// Components
import { StepProgress } from './components/layout';

// Step paths for navigation
const stepPaths = ['/type', '/upload', '/analysis', '/book-setup'];

// Initialize Google Analytics on app load
initGA();
initMixpanel();

// Component to track page views (must be inside Router)
function PageTracker({ children }) {
  usePageTracking();
  return children;
}

function ScrollToTop() {
  const { pathname } = useLocation();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function App() {
  return (
    <YearbookProvider>
      <Router>
        <ScrollToTop />
        <PageTracker>
        <div className="min-h-screen bg-cream paper-bg">
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route
              path="/type"
              element={
                <PageWithProgress step={1}>
                  <TypeSelectPage />
                </PageWithProgress>
              }
            />
            <Route
              path="/upload"
              element={
                <PageWithProgress step={2}>
                  <UploadPage />
                </PageWithProgress>
              }
            />
            <Route
              path="/analysis"
              element={
                <PageWithProgress step={3}>
                  <AnalysisPage />
                </PageWithProgress>
              }
            />
            <Route
              path="/book-setup"
              element={
                <PageWithProgress step={4}>
                  <BookSetupPage />
                </PageWithProgress>
              }
            />
            <Route path="/preview" element={<PreviewPage />} />
            {/* Catch-all route - redirect any unknown paths to home */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
        </PageTracker>
      </Router>
    </YearbookProvider>
  );
}

// Wrapper component that adds the step progress indicator
function PageWithProgress({ step, children }) {
  const navigate = useNavigate();
  const location = useLocation();
  
  const currentStepIndex = stepPaths.indexOf(location.pathname);
  const previousPath = currentStepIndex > 0 ? stepPaths[currentStepIndex - 1] : null;
  
  const handleBack = () => {
    if (previousPath) {
      navigate(previousPath);
    }
  };
  
  const handleHome = () => {
    navigate('/');
  };
  
  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-white/80 backdrop-blur-sm border-b border-parchment sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          {/* Navigation row */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-4">
              {previousPath && (
                <button
                  onClick={handleBack}
                  className="flex items-center gap-1.5 min-w-[44px] min-h-[44px] justify-center px-3 py-1.5 text-sm text-sepia 
                           hover:text-rust hover:bg-rust/5 rounded-lg transition-colors"
                  aria-label="Go to previous step"
                >
                  <ArrowLeftIcon className="w-4 h-4" />
                  <span className="hidden sm:inline">Back</span>
                </button>
              )}
              <button
                onClick={handleHome}
                className="flex items-center hover:opacity-80 transition-opacity"
                aria-label="Go to home"
              >
                <Logo textClassName="text-lg" />
              </button>
            </div>
            <button
              onClick={handleHome}
              className="flex items-center gap-1.5 min-w-[44px] min-h-[44px] justify-center px-3 py-1.5 text-sm text-sepia 
                       hover:text-rust hover:bg-rust/5 rounded-lg transition-colors"
              aria-label="Start over"
            >
              <HomeIcon className="w-4 h-4" />
              <span className="hidden sm:inline">Start Over</span>
            </button>
          </div>
          {/* Step progress */}
          <StepProgress currentStep={step} />
        </div>
      </header>
      <main className="flex-1">
        {children}
      </main>
    </div>
  );
}

export default App;
